// Три темы фона урока — эталонная дизайн-система (Figma «OA Studio App», канвас
// `дизайн-система | шаблон final`). Модуль знает ТОЛЬКО про фоны и ничего про
// элементы блока: оформление элементов живёт в blockTheme.designBlock.
//
// Тема хранится в `bgColor` СВОИМ ЯКОРНЫМ ХЕКСОМ, а не ключом вроде "theme2".
// Причина внешняя: то же поле читает oa-mobile и кладёт его в RN-`backgroundColor`,
// а RN на невалидном цвете падает в рантайме. Якорный хекс валиден везде —
// чужие рендереры показывают плоский цвет, наш достраивает градиент.

import { uiText } from '../i18n'

export type LessonThemeId = 'theme1' | 'theme2' | 'theme3'

export interface LessonTheme {
  id: LessonThemeId
  label: string
  /** Значение, которое уходит в `bgColor`. Всегда с решёткой, всегда upper-case. */
  anchor: string
  /** Готовое CSS-значение `background`. */
  background: string
}

/** Таблица ведётся ТОЛЬКО дополнением: якорный хекс — это ключ, по которому
 *  опознаются уже сохранённые блоки. Удалить строку значит потерять тему у всех
 *  курсов, где её выбрали. */
/** Подпись читается геттером — на языке интерфейса в момент обращения. */
const LABELS = {
  ru: { theme1: 'Кремовая', theme2: 'Персиковая', theme3: 'Лавандовая' },
  en: { theme1: 'Cream', theme2: 'Peach', theme3: 'Lavender' },
}

export const LESSON_THEMES: readonly LessonTheme[] = [
  {
    id: 'theme1',
    get label() {
      return uiText(LABELS).theme1
    },
    anchor: '#FFFCF5',
    background: '#FFFCF5',
  },
  {
    id: 'theme2',
    get label() {
      return uiText(LABELS).theme2
    },
    anchor: '#FFE7DA',
    background: 'linear-gradient(158.1deg, #FFFCF5 0%, #FFE7DA 100%)',
  },
  {
    id: 'theme3',
    get label() {
      return uiText(LABELS).theme3
    },
    anchor: '#D0C4FF',
    background: 'linear-gradient(158.1deg, #D0C4FF 0%, #FFFCF5 100%)',
  },
] as const

export const DEFAULT_LESSON_THEME: LessonTheme = LESSON_THEMES[0]

/** Хекс без решётки — 3/4/6/8 hex-цифр (RGB/RGBA/RRGGBB/RRGGBBAA). */
const BARE_HEX = /^[0-9a-fA-F]{3,4}$|^[0-9a-fA-F]{6}$|^[0-9a-fA-F]{8}$/

/** Приводит авторский `bgColor` к сравнимому виду: бек формат не валидирует
 *  (контракт §2), поэтому решётки может не быть, а регистр — любой. Решётку
 *  приписываем, только когда остаток похож на хекс: легаси-значение вроде
 *  `red` или `rgb(1,2,3)` решёткой не тронуто, иначе `#red` — невалидный CSS,
 *  и объявление `background` отбрасывается целиком (блок остаётся без фона). */
const normalizeHex = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('#')) return trimmed
  return BARE_HEX.test(trimmed) ? `#${trimmed}` : trimmed
}

const findTheme = (bgColor: string | null | undefined): LessonTheme | null => {
  if (!bgColor) return null
  const hex = normalizeHex(bgColor).toUpperCase()
  return LESSON_THEMES.find((t) => t.anchor === hex) ?? null
}

/** CSS-значение `background` для блока. Пусто — тема 1; якорный хекс — рецепт
 *  своей темы; всё остальное (цвет из старых курсов) — как есть, плоским. */
export const resolveLessonBackground = (bgColor: string | null | undefined): string => {
  const theme = findTheme(bgColor)
  if (theme) return theme.background
  if (!bgColor?.trim()) return DEFAULT_LESSON_THEME.background
  return normalizeHex(bgColor)
}

/** Какая кнопка темы подсвечена в панели. `null` — «тему не опознали»: пустое
 *  значение (новый блок до засева/старые данные) и незнакомый легаси-цвет
 *  оба сюда попадают. ВАЖНО: сохранение чужой цвет не переписывает — если бы
 *  тут молча подставлялась тема 1, пикер лгал бы, что выбор сделан, хотя в
 *  данных всё ещё лежит исходный цвет. Честное «ничего не выбрано» — на
 *  вызывающей стороне (RadioCardGroup уже умеет value: string | null). */
export const resolveLessonThemeId = (bgColor: string | null | undefined): LessonThemeId | null =>
  findTheme(bgColor)?.id ?? null
